import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from 'react';
import { useProjectStore } from '../store/project-store';
import { useAuthStore } from '../store/auth-store';
import { Avatar } from '../components/Avatar';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../store/ui-store';
import { formatLocaleDate } from '../utils/locale';
import { formatRelativeTime } from '../utils/time';
import { filterTasksByRole, getAccessibleProjectIds, parseChangeLog, } from '../utils/access-control';
function getTaskTitle(taskId, tasks) {
    return tasks.find((t) => t.id === taskId)?.title || taskId;
}
function buildChangeLogActivities(projectId, rawChangeLog, tasks, t) {
    const entries = parseChangeLog(rawChangeLog);
    const items = [];
    for (const entry of entries) {
        const taskTitle = getTaskTitle(entry.target, tasks);
        let type;
        let message;
        switch (entry.action) {
            case 'status-change':
                type = 'status_change';
                message = `${t('activity.movedTo')} ${entry.details.split(' -> ')[1]?.split(' → ').pop()?.trim() || entry.details}`;
                break;
            case 'task-created':
                type = 'created';
                message = 'created task';
                break;
            case 'task-assigned':
                type = 'assignment';
                message = entry.details;
                break;
            default:
                continue;
        }
        items.push({
            type,
            taskId: entry.target,
            taskTitle,
            projectId,
            user: entry.user.startsWith('@') ? entry.user : `@${entry.user}`,
            message,
            timestamp: entry.timestamp,
        });
    }
    return items;
}
function buildDerivedStatusChanges(tasks, t) {
    const items = [];
    for (const task of tasks) {
        const changedBy = task.lastUpdatedBy
            ? `@${task.lastUpdatedBy}`
            : undefined;
        items.push({
            type: 'status_change',
            taskId: task.id,
            taskTitle: task.title,
            projectId: task.projectId,
            user: changedBy || '@system',
            message: `${t('activity.movedTo')} ${task.status.replace('-', ' ')}`,
            timestamp: task.updated,
        });
    }
    return items;
}
function buildCommentActivities(tasks, matchName, t) {
    const items = [];
    for (const task of tasks) {
        for (const comment of task.comments) {
            if (comment.user === matchName)
                continue;
            items.push({
                type: 'comment',
                taskId: task.id,
                taskTitle: task.title,
                projectId: task.projectId,
                user: comment.user,
                message: `${t('activity.commentedOn')} "${task.title}"`,
                timestamp: comment.timestamp,
            });
        }
    }
    return items;
}
export function ActivityFeed() {
    const { t } = useTranslation();
    const locale = useUIStore((state) => state.locale);
    const user = useAuthStore((s) => s.user);
    const tasks = useProjectStore((s) => s.tasks);
    const projects = useProjectStore((s) => s.projects);
    const sections = useProjectStore((s) => s._sections);
    const loadAllProjectSections = useProjectStore((s) => s.loadAllProjectSections);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 20;
    useEffect(() => {
        const client = useAuthStore.getState().getClient();
        if (client) {
            loadAllProjectSections(client);
        }
    }, [loadAllProjectSections]);
    const allActivityItems = useMemo(() => {
        if (!user)
            return [];
        const matchName = `@${user.username}`;
        const items = [];
        const accessibleProjectIds = getAccessibleProjectIds(projects, user, tasks, sections);
        const tasksWithChangelog = new Set();
        for (const projectId of accessibleProjectIds) {
            const rawChangeLog = sections[projectId]?.['change log'] ?? '';
            if (rawChangeLog) {
                const logItems = buildChangeLogActivities(projectId, rawChangeLog, tasks, t);
                for (const item of logItems) {
                    tasksWithChangelog.add(item.taskId);
                    items.push(item);
                }
            }
        }
        const visibleTasks = filterTasksByRole(tasks, user, sections);
        const commentItems = buildCommentActivities(visibleTasks, matchName, t);
        items.push(...commentItems);
        const tasksNeedingFallback = visibleTasks.filter((t) => !tasksWithChangelog.has(t.id));
        const fallbackItems = buildDerivedStatusChanges(tasksNeedingFallback, t);
        items.push(...fallbackItems);
        items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        return items;
    }, [tasks, user, sections, projects, t]);
    const totalPages = Math.max(1, Math.ceil(allActivityItems.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pagedItems = allActivityItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const stats = useMemo(() => {
        const total = allActivityItems.length;
        const statusChanges = allActivityItems.filter((i) => i.type === 'status_change').length;
        const comments = allActivityItems.filter((i) => i.type === 'comment').length;
        const assignments = allActivityItems.filter((i) => i.type === 'assignment').length;
        const created = allActivityItems.filter((i) => i.type === 'created').length;
        return { total, statusChanges, comments, assignments, created };
    }, [allActivityItems]);
    const getProjectName = (projectId) => {
        if (!projectId)
            return '';
        return projects.find((p) => p.project_id === projectId)?.name || '';
    };
    if (!user)
        return null;
    const commentItems = pagedItems.filter((i) => i.type === 'comment');
    const otherItems = pagedItems.filter((i) => i.type !== 'comment');
    return (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "flex items-center justify-between", children: _jsx("h2", { className: "text-2xl font-bold text-text-primary", children: t('activity.title') }) }), _jsxs("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4", children: [_jsxs("div", { className: "card p-4", children: [_jsx("p", { className: "text-sm text-text-muted", children: "Total" }), _jsx("p", { className: "text-2xl font-bold text-text-primary", children: stats.total })] }), _jsxs("div", { className: "card p-4", children: [_jsx("p", { className: "text-sm text-text-muted", children: "Status Changes" }), _jsx("p", { className: "text-2xl font-bold text-status-progress", children: stats.statusChanges })] }), _jsxs("div", { className: "card p-4", children: [_jsx("p", { className: "text-sm text-text-muted", children: "Comments" }), _jsx("p", { className: "text-2xl font-bold text-status-review", children: stats.comments })] }), _jsxs("div", { className: "card p-4", children: [_jsx("p", { className: "text-sm text-text-muted", children: "Assignments" }), _jsx("p", { className: "text-2xl font-bold text-status-done", children: stats.assignments })] })] }), commentItems.length > 0 && (_jsxs("div", { children: [_jsxs("h3", { className: "text-sm font-semibold text-text-secondary mb-3", children: [t('activity.commentsOnYourTasks'), " (", commentItems.length, ")"] }), _jsx("div", { className: "space-y-2", children: commentItems.map((item, i) => (_jsx("div", { className: "card p-4 border-l-4 border-l-primary", children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx(Avatar, { name: item.user, size: "w-8 h-8" }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm font-medium text-text-primary", children: item.user }), _jsxs("span", { className: "text-xs text-text-muted", children: [formatRelativeTime(item.timestamp, t), " \u2022 ", formatLocaleDate(item.timestamp, locale)] })] }), _jsx("p", { className: "text-sm text-text-secondary mt-0.5", children: item.message }), _jsxs("div", { className: "flex items-center gap-2 mt-1", children: [_jsx("span", { className: "text-xs text-text-muted font-mono", children: item.taskId }), _jsx("span", { className: "text-xs text-text-secondary truncate", children: item.taskTitle }), item.projectId && (_jsx("span", { className: "text-xs text-primary", children: getProjectName(item.projectId) }))] })] })] }) }, i))) })] })), _jsxs("div", { children: [_jsx("h3", { className: "text-sm font-semibold text-text-secondary mb-3", children: t('activity.allActivity') }), allActivityItems.length === 0 ? (_jsx("div", { className: "card p-8 text-center", children: _jsx("p", { className: "text-text-muted", children: t('activity.noActivityYet') }) })) : (_jsx("div", { className: "space-y-2", children: otherItems.map((item, i) => (_jsx("div", { className: "card p-3", children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Avatar, { name: item.user, size: "w-6 h-6" }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("p", { className: "text-sm text-text-secondary", children: [_jsx("span", { className: "font-medium text-text-primary", children: item.user }), ' ', item.message] }), _jsxs("div", { className: "flex items-center gap-2 mt-0.5", children: [_jsx("span", { className: "text-xs text-text-muted font-mono", children: item.taskId }), _jsx("span", { className: "text-xs text-text-secondary truncate", children: item.taskTitle }), item.projectId && (_jsx("span", { className: "text-xs text-primary", children: getProjectName(item.projectId) })), _jsxs("span", { className: "text-xs text-text-muted", children: [formatRelativeTime(item.timestamp, t), " \u2022 ", formatLocaleDate(item.timestamp, locale)] })] })] })] }) }, i))) })), totalPages > 1 && (_jsxs("div", { className: "flex items-center justify-center gap-3 mt-4", children: [_jsx("button", { onClick: () => setPage((p) => Math.max(1, p - 1)), disabled: safePage <= 1, className: "px-3 py-1.5 text-sm rounded border border-border disabled:opacity-40 hover:bg-bg-secondary transition-colors", children: "Previous" }), _jsxs("span", { className: "text-sm text-text-muted", children: ["Page ", safePage, " of ", totalPages] }), _jsx("button", { onClick: () => setPage((p) => Math.min(totalPages, p + 1)), disabled: safePage >= totalPages, className: "px-3 py-1.5 text-sm rounded border border-border disabled:opacity-40 hover:bg-bg-secondary transition-colors", children: "Next" })] }))] })] }));
}
//# sourceMappingURL=ActivityFeed.js.map